import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { TabPanel, TabView } from 'primereact/tabview'
import { FC, WheelEvent, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import CfdReport from '../components/dashboard/CFDReport'
import ConvergenceAnalysis from '../components/dashboard/ConvergenceAnalysis'
import GroupForceAnalysis from '../components/dashboard/GroupForceAnalysis'
import HeadlineData from '../components/dashboard/HeadlineData'
import ReportGenerator from '../components/dashboard/ReportGenerator'
import RunMedia from '../components/dashboard/RunMedia'
import PlotTabContainer from '../components/dashboard/plot/PlotTabContainer'
import {
  PLOT_FILTER_SELECTION_KEY,
  PLOT_PROJECT_RUN_SELECTION_KEY
} from '../components/dashboard/ProjectRunSelector'
import SolveMetrics from '../components/dashboard/SolveMetrics'
import MethodologyReport from '../components/dashboard/methodology-report'
import { NASCARTeams } from '../utils/constants'
import {
  currentTeamState,
  currentUserState,
  reportGeneratorRunState,
  runMediaRunState
} from '../utils/state-atoms'
import SubmissionCoefficient from '../components/dashboard/SubmissionCoefficient'

const tabPanelStyles = {
  root: {
    className: 'p-0 border-none '
  },
  header: {
    className: 'border-b border-gray-300 flex items-center gap-2'
  },
  headerAction: ({ context }: any) => {
    return {
      className:
        ((context.active as any) ? '!text-gm-blue !border-b-2 !border-gm-blue' : 'text-gray-500') +
        ' hover:!text-gm-blue font-bold hover:!border-gm-blue'
    }
  },
  content: {
    className: 'dark:!bg-transparent px-0'
  }
}

const PLOT_ARRAY_STORAGE_KEY = 'plot_array'

const wholeNumberFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 0
})
const twoDecimalFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 2
})

const getFiniteNumber = (value: number | string | null): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim().replace(/,/g, '')
    if (normalizedValue === '') return null

    const number = Number(normalizedValue)
    return Number.isFinite(number) ? number : null
  }

  return null
}

const formatWholeNumber = (value: number | string | null): string | number => {
  const number = getFiniteNumber(value)
  return number === null ? value || '' : wholeNumberFormatter.format(number)
}

const formatTwoDecimalNumber = (value: number | string | null): string | number => {
  const number = getFiniteNumber(value)
  return number === null ? value || '' : twoDecimalFormatter.format(number)
}

const Dashboard: FC = () => {
  const currentUser = useRecoilValue(currentUserState)
  const currentTeam = useRecoilValue(currentTeamState)
  const [searchParams, setSearchParams] = useSearchParams()
  const setReportRun = useSetRecoilState(reportGeneratorRunState)
  const setRunMediaRun = useSetRecoilState(runMediaRunState)
  const showComplianceTab =
    NASCARTeams.includes(currentTeam) || currentTeam?.toLowerCase().startsWith('test')
  const showSolveMetricsTab = ['admin', 'developer'].includes(
    currentUser?.role?.roleId?.toLowerCase() || ''
  )
  const showConvergenceAnalysisTab = true
  const showSubmissionPlottingTab =
    currentTeam === 'NASCAR-Cup-28' || currentTeam?.toLowerCase().startsWith('testing-team')
  // Group Force is available to all teams.
  const showGroupForceTab = true
  // Methodology Report is restricted to privileged methodology roles.
  const showMethodologyReportTab = ['admin', 'developer', 'methodology-specialist'].includes(
    currentUser?.role?.roleId?.toLowerCase() || ''
  )
  const [plotTabs, setPlotTabs] = useState<
    { label: string; selectedCases: Record<string, any>; key: string }[]
  >(() =>
    localStorage.getItem(PLOT_ARRAY_STORAGE_KEY)
      ? JSON.parse(localStorage.getItem(PLOT_ARRAY_STORAGE_KEY) || '[]')
      : [{ label: '', selectedCases: {}, key: Date.now().toString() }]
  )
  const [editTabModalVisible, setEditTabModalVisible] = useState(false)
  const [editingTabIndex, setEditingTabIndex] = useState<number | null>(null)
  const [editedLabel, setEditedLabel] = useState('')
  // Track the active tab by stable key, not positional index: the static tabs (CFD,
  // Submission, Solve Metrics) are derived from async-loaded team/role, so a numeric index
  // would silently point at a different tab once those tabs appear and shift the plot tabs.
  const [activeTabKey, setActiveTabKey] = useState<string>('headline-data')
  const requestedTab = searchParams.get('activeTab') || searchParams.get('tab') || 'headline-data'
  const tabKeys = useMemo(() => {
    const keys = ['headline-data', 'report-generator', 'run-media']
    // Each conditional tab below occupies a fixed child slot in <TabView> even when hidden
    // (it renders `false`, which PrimeReact's React.Children-based indexing still counts).
    // Push a placeholder for hidden tabs so this key list stays aligned with the raw child
    // indices PrimeReact reports in onTabChange / expects in activeIndex. Compacting here
    // (skipping hidden tabs) would shift every later tab and break clicking them.
    keys.push(showComplianceTab ? 'cfd-report' : '__hidden-cfd-report')
    keys.push(showSolveMetricsTab ? 'solve-metrics' : '__hidden-solve-metrics')
    keys.push(showSubmissionPlottingTab ? 'submission-coefficient' : '__hidden-submission')
    keys.push(showGroupForceTab ? 'group-force-analysis' : '__hidden-group-force')
    keys.push(
      showConvergenceAnalysisTab ? 'convergence-analysis' : '__hidden-convergence-analysis'
    )
    keys.push(showMethodologyReportTab ? 'methodology-report' : '__hidden-methodology-report')
    plotTabs.forEach(plot => keys.push(plot.key))
    return keys
  }, [
    showComplianceTab,
    showSubmissionPlottingTab,
    showGroupForceTab,
    showSolveMetricsTab,
    showConvergenceAnalysisTab,
    showMethodologyReportTab,
    plotTabs
  ])
  const activeTabIndex = Math.max(tabKeys.indexOf(activeTabKey), 0)

  useEffect(() => {
    const nextTab = tabKeys.includes(requestedTab) ? requestedTab : 'headline-data'
    if (activeTabKey !== nextTab) {
      setActiveTabKey(nextTab)
    }
  }, [activeTabKey, requestedTab, tabKeys])

  const handleTabNavWheel = (event: WheelEvent<HTMLDivElement>) => {
    const tabNav = event.currentTarget

    if (
      tabNav.scrollWidth <= tabNav.clientWidth ||
      Math.abs(event.deltaY) <= Math.abs(event.deltaX)
    ) {
      return
    }

    event.preventDefault()
    tabNav.scrollLeft += event.deltaY
  }

  const handleOpenEditModal = (index: number, currentLabel: string) => {
    setEditingTabIndex(index)
    setEditedLabel(currentLabel)
    setEditTabModalVisible(true)
  }

  const handleSaveLabel = () => {
    if (editingTabIndex !== null && editedLabel.trim()) {
      const updatedTabs = [...plotTabs]
      updatedTabs[editingTabIndex].label = editedLabel.trim()
      setPlotTabs(updatedTabs)
      localStorage.setItem(PLOT_ARRAY_STORAGE_KEY, JSON.stringify(updatedTabs))
    }
    setEditTabModalVisible(false)
    setEditingTabIndex(null)
    setEditedLabel('')
  }

  return (
    <div className="w-full flex flex-col items-stretch">
      <div className="p-4 text-gm-blue font-bold">Welcome, {currentUser.firstName}</div>

      <TabView
        className="w-full"
        scrollable
        pt={{
          root: { className: 'bg-transparent' },
          navContainer: { className: 'flex items-stretch w-full' },
          navContent: {
            className: 'min-w-0 flex-1 overflow-x-auto',
            onWheel: handleTabNavWheel
          },
          prevButton: { className: '!static !w-8 shrink-0 self-stretch' },
          nextButton: { className: '!static !w-8 shrink-0 self-stretch' },
          nav: { className: 'flex-nowrap' }
        }}
        activeIndex={activeTabIndex}
        onTabChange={e => {
          const nextTab = tabKeys[e.index]
          setActiveTabKey(nextTab)
          setSearchParams(prev => {
            const params = new URLSearchParams(prev)
            params.set('activeTab', nextTab)
            params.delete('tab')
            return params
          })
        }}>
        <TabPanel
          header="Headline Data"
          key="headline-data"
          pt={tabPanelStyles}
          headerTemplate={options => (
            <button
              className={`min-h-full flex items-center space-x-2 px-4 cursor-pointer !-mb-[2px] border-b-2 ${
                options.selected
                  ? '!text-gm-blue !border-gm-blue'
                  : 'text-gray-500 border-transparent'
              } hover:!text-gm-blue hover:!border-gm-blue font-bold`}
              onClick={options.onClick}>
              <span className="leading-none whitespace-nowrap">Headline Data</span>
            </button>
          )}>
          <HeadlineData
            onNewPlot={plotData => {
              // save selection to session storage so that it can be accessed in the PlotTabContainer when user clicks "Plot Data"
              // it will be remove after first read in the PlotTabContainer to avoid stale selection in case user changes selection
              // in HeadlineData but forgets to click "Plot Data" again
              console.log('Received plot data from HeadlineData: ', plotData)
              sessionStorage.setItem(
                PLOT_PROJECT_RUN_SELECTION_KEY,
                JSON.stringify(plotData.projectRunSelection)
              )
              sessionStorage.setItem(
                PLOT_FILTER_SELECTION_KEY,
                JSON.stringify({ states: plotData.states, baselines: plotData.baselines })
              )
              const newTab = { label: '', selectedCases: {}, key: Date.now().toString() }
              const updatedTabs = [...plotTabs, newTab]
              setPlotTabs(updatedTabs)
              setActiveTabKey(newTab.key) // switch to the new plot tab
              localStorage.setItem(PLOT_ARRAY_STORAGE_KEY, JSON.stringify(updatedTabs))
            }}
            onGenerateReport={run => {
              setReportRun(run)
              setActiveTabKey('report-generator')
            }}
            onGoToMedia={run => {
              setRunMediaRun(run)
              setActiveTabKey('run-media')
            }}
          />
        </TabPanel>
        <TabPanel
          key="report-generator"
          header="Report Generator"
          pt={tabPanelStyles}
          headerTemplate={options => (
            <button
              className={`min-h-full flex items-center space-x-2 px-4 cursor-pointer !-mb-[2px] border-b-2 ${
                options.selected
                  ? '!text-gm-blue !border-gm-blue'
                  : 'text-gray-500 border-transparent'
              } hover:!text-gm-blue hover:!border-gm-blue font-bold`}
              onClick={options.onClick}>
              <span className="leading-none whitespace-nowrap">Report Generator</span>
              <span className="text-[10px] font-bold leading-none px-1.5 py-0.5 rounded bg-gm-blue/10 text-gm-blue uppercase tracking-wide">
                Beta
              </span>
            </button>
          )}>
          <ReportGenerator />
        </TabPanel>
        <TabPanel
          key="run-media"
          header="Run Media"
          pt={tabPanelStyles}
          headerTemplate={options => (
            <button
              className={`min-h-full flex items-center space-x-2 px-4 cursor-pointer !-mb-[2px] border-b-2 ${
                options.selected
                  ? '!text-gm-blue !border-gm-blue'
                  : 'text-gray-500 border-transparent'
              } hover:!text-gm-blue hover:!border-gm-blue font-bold`}
              onClick={options.onClick}>
              <span className="leading-none whitespace-nowrap">Run Media</span>
            </button>
          )}>
          <RunMedia
            onBuildReport={run => {
              setReportRun(run)
              setActiveTabKey('report-generator')
            }}
          />
        </TabPanel>
        {showComplianceTab && (
          <TabPanel
            key="cfd-report"
            header="CFD Report and Stats"
            pt={tabPanelStyles}
            headerTemplate={options => (
              <button
                className={`min-h-full flex items-center space-x-2 px-4 cursor-pointer !-mb-[2px] border-b-2 ${
                  options.selected
                    ? '!text-gm-blue !border-gm-blue'
                    : 'text-gray-500 border-transparent'
                } hover:!text-gm-blue hover:!border-gm-blue font-bold`}
                onClick={options.onClick}>
                <span className="leading-none whitespace-nowrap">CFD Report and Stats</span>
              </button>
            )}>
            <CfdReport />
          </TabPanel>
        )}
        {showSolveMetricsTab && (
          <TabPanel
            key="solve-metrics"
            header="Solve Metrics"
            pt={tabPanelStyles}
            headerTemplate={options => (
              <button
                className={`min-h-full flex items-center space-x-2 px-4 cursor-pointer !-mb-[2px] border-b-2 ${
                  options.selected
                    ? '!text-gm-blue !border-gm-blue'
                    : 'text-gray-500 border-transparent'
                } hover:!text-gm-blue hover:!border-gm-blue font-bold`}
                onClick={options.onClick}>
                <span className="leading-none whitespace-nowrap">Solve Metrics</span>
              </button>
            )}>
            <SolveMetrics
              formatWholeNumber={formatWholeNumber}
              formatTwoDecimalNumber={formatTwoDecimalNumber}
            />
          </TabPanel>
        )}
        {showSubmissionPlottingTab && (
          <TabPanel
            key="submission-coefficient"
            header="Submission Plotting"
            pt={tabPanelStyles}
            headerTemplate={options => (
              <button
                className={`min-h-full flex items-center space-x-2 px-4 cursor-pointer !-mb-[2px] border-b-2 ${
                  options.selected
                    ? '!text-gm-blue !border-gm-blue'
                    : 'text-gray-500 border-transparent'
                } hover:!text-gm-blue hover:!border-gm-blue font-bold`}
                onClick={options.onClick}>
                <span className="leading-none whitespace-nowrap">Submission Plotting</span>
              </button>
            )}>
            <SubmissionCoefficient />
          </TabPanel>
        )}
        {showGroupForceTab && (
          <TabPanel
            key="group-force-analysis"
            header="Group Force Analysis"
            pt={tabPanelStyles}
            headerTemplate={options => (
              <button
                className={`min-h-full flex items-center space-x-2 px-4 cursor-pointer !-mb-[2px] border-b-2 ${
                  options.selected
                    ? '!text-gm-blue !border-gm-blue'
                    : 'text-gray-500 border-transparent'
                } hover:!text-gm-blue hover:!border-gm-blue font-bold`}
                onClick={options.onClick}>
                <span className="leading-none whitespace-nowrap">Group Force Analysis</span>
              </button>
            )}>
            <GroupForceAnalysis />
          </TabPanel>
        )}
        {showConvergenceAnalysisTab && (
          <TabPanel
            key="convergence-analysis"
            header="Convergence Analysis"
            pt={tabPanelStyles}
            headerTemplate={options => (
              <button
                className={`min-h-full flex items-center space-x-2 px-4 cursor-pointer !-mb-[2px] border-b-2 ${
                  options.selected
                    ? '!text-gm-blue !border-gm-blue'
                    : 'text-gray-500 border-transparent'
                } hover:!text-gm-blue hover:!border-gm-blue font-bold`}
                onClick={options.onClick}>
                <span className="leading-none whitespace-nowrap">Convergence Analysis</span>
                <span className="text-[10px] font-bold leading-none px-1.5 py-0.5 rounded bg-gm-blue/10 text-gm-blue uppercase tracking-wide">
                  Beta
                </span>
              </button>
            )}>
            <ConvergenceAnalysis />
          </TabPanel>
        )}
        {showMethodologyReportTab && (
          <TabPanel
            key="methodology-report"
            header="Methodology Report"
            pt={tabPanelStyles}
            headerTemplate={options => (
              <button
                className={`min-h-full flex items-center space-x-2 px-4 cursor-pointer !-mb-[2px] border-b-2 ${
                  options.selected
                    ? '!text-gm-blue !border-gm-blue'
                    : 'text-gray-500 border-transparent'
                } hover:!text-gm-blue hover:!border-gm-blue font-bold`}
                onClick={options.onClick}>
                <span className="leading-none whitespace-nowrap">Methodology Report</span>
                <span className="text-[10px] font-bold leading-none px-1.5 py-0.5 rounded bg-gm-blue/10 text-gm-blue uppercase tracking-wide">
                  Beta
                </span>
              </button>
            )}>
            <MethodologyReport />
          </TabPanel>
        )}
        {plotTabs.map((plot, plotIndex) => (
          <TabPanel
            key={plot.key}
            header={plot.label}
            pt={tabPanelStyles}
            headerTemplate={options => {
              const isActive = options.selected
              return (
                <div
                  className={`group min-h-full flex items-center space-x-2 px-4 cursor-pointer !-mb-[1px] border-b-2 ${
                    isActive ? '!text-gm-blue !border-gm-blue' : 'text-gray-500 border-transparent'
                  } hover:!text-gm-blue hover:!border-gm-blue font-bold`}
                  onClick={options.onClick}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      options.onClick(e as any)
                    }
                  }}
                  role="button"
                  tabIndex={0}>
                  <span className="leading-none whitespace-nowrap">
                    {plot.label || `Plot ${plotTabs.length > 1 ? plotIndex + 1 : ''}`}
                  </span>
                  {plotIndex === plotTabs.length - 1 && (
                    <button
                      className="ml-2 !bg-transparent p-1 hover:text-gm-blue text-gray-300 transition-colors duration-200"
                      onClick={e => {
                        e.stopPropagation()
                        const updatedTabs = [
                          ...plotTabs,
                          { label: '', selectedCases: {}, key: Date.now().toString() }
                        ]
                        setPlotTabs(updatedTabs)
                        localStorage.setItem(PLOT_ARRAY_STORAGE_KEY, JSON.stringify(updatedTabs))
                      }}>
                      <i className="pi pi-plus text-[0.75rem]"></i>
                    </button>
                  )}
                  {plotTabs.length > 1 && (
                    <button
                      className="ml-2 !bg-transparent p-1 text-gray-300 hidden pointer-events-none group-hover:inline-block group-hover:pointer-events-auto hover:text-red-500 transition-colors duration-200"
                      onClick={e => {
                        e.stopPropagation()
                        if (plotTabs.length > 1) {
                          const updatedTabs = plotTabs.filter((_, i) => i !== plotIndex)
                          if (plot.key === activeTabKey) {
                            const fallback = updatedTabs[Math.max(plotIndex - 1, 0)]
                            setActiveTabKey(fallback ? fallback.key : 'headline-data')
                          }
                          setPlotTabs(updatedTabs)
                          localStorage.setItem(PLOT_ARRAY_STORAGE_KEY, JSON.stringify(updatedTabs))
                        }
                      }}>
                      <i className="pi pi-times text-[0.75rem]"></i>
                    </button>
                  )}
                  <button
                    className="ml-2 !bg-transparent p-1 text-gray-300 hidden pointer-events-none group-hover:inline-block group-hover:pointer-events-auto hover:text-gm-blue transition-colors duration-200"
                    onClick={e => {
                      e.stopPropagation()
                      handleOpenEditModal(plotIndex, plot.label)
                    }}>
                    <i className="pi pi-pencil text-[0.75rem]"></i>
                  </button>
                </div>
              )
            }}>
            <PlotTabContainer
              plotIndex={plotIndex}
              plotKey={plot.key}
              key={`plot-tab-${plot.key}`}
            />
          </TabPanel>
        ))}
      </TabView>

      <Dialog
        header="Edit Tab Name"
        visible={editTabModalVisible}
        onHide={() => {
          setEditTabModalVisible(false)
          setEditingTabIndex(null)
          setEditedLabel('')
        }}
        style={{ width: '400px' }}
        footer={
          <div className="flex justify-end space-x-2">
            <button
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
              onClick={() => setEditTabModalVisible(false)}>
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-gm-blue text-white rounded hover:bg-blue-700"
              onClick={handleSaveLabel}
              disabled={!editedLabel.trim()}>
              Save
            </button>
          </div>
        }>
        <div className="flex flex-col space-y-2">
          <label htmlFor="tab-name" className="font-semibold">
            Tab Name
          </label>
          <InputText
            id="tab-name"
            value={editedLabel}
            onChange={e => setEditedLabel(e.target.value)}
            placeholder="Enter tab name"
            autoFocus
            onKeyDown={e => {
              if (e.key === 'Enter' && editedLabel && editedLabel.trim()) {
                handleSaveLabel()
              }
            }}
          />
        </div>
      </Dialog>
    </div>
  )
}

export default Dashboard
